import React, { useState, useEffect, useCallback } from 'react';
import { Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { systemService } from '../../services/systemService';
import { roleService } from '../../services/roleService';
import { useCatalog } from '../../lib/permissions';
import CustomSelect from '../shared/CustomSelect';

// Import all components
import SystemUsersList from './SystemUsersList';
import SystemUserDetailsView from './SystemUserDetailsView';
import SystemUserFormPage from './SystemUserFormPage';
import SystemUserFilters from './SystemUserFilters';
import SystemUserStats from './SystemUserStats';
import { useAuth } from '../../context/AuthContext';
import { DeleteModal } from '../shared/DeleteModal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

const SystemUsersContent = () => {
    const { hasPermission } = useAuth();

    // State Management
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [roles, setRoles] = useState([]);
    const { catalog } = useCatalog();

    // Pagination State
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [paginating, setPaginating] = useState(false);

    // UI State
    const [selectedUser, setSelectedUser] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
    const [actionLoading, setActionLoading] = useState(false);

    // Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch data when filters or pagination changes
    useEffect(() => {
        fetchUsers();
    }, [currentPage, pageSize, debouncedSearchTerm, roleFilter, statusFilter]);

    useEffect(() => {
        roleService.getAll().then((res) => setRoles(res?.data || [])).catch(() => setRoles([]));
    }, []);

    const fetchUsers = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');

            // Fetch all users first
            const response = await systemService.getAll();

            if (response.success && response.data) {
                let allUsers = response.data;
                setStats({
                    totalUsers: allUsers.length,
                    activeUsers: allUsers.filter(u => u.status === 'ACTIVE').length,
                    suspendedUsers: allUsers.filter(u => u.status === 'SUSPENDED').length,
                    lockedUsers: allUsers.filter(u => u.status === 'LOCKED').length,
                });

                // Apply client-side filtering
                if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
                    const searchLower = debouncedSearchTerm.toLowerCase();
                    allUsers = allUsers.filter(user =>
                        user.firstName?.toLowerCase().includes(searchLower) ||
                        user.lastName?.toLowerCase().includes(searchLower) ||
                        user.email?.toLowerCase().includes(searchLower)
                    );
                }

                if (roleFilter !== 'ALL') {
                    allUsers = allUsers.filter(user => user.roleName === roleFilter);
                }

                if (statusFilter !== 'ALL') {
                    allUsers = allUsers.filter(user =>
                        statusFilter === 'INACTIVE' ? user.status !== 'ACTIVE' : user.status === statusFilter);
                }

                // Apply client-side pagination
                const start = currentPage * pageSize;
                const paginatedUsers = allUsers.slice(start, start + pageSize);

                setUsers(paginatedUsers);
                setTotalElements(allUsers.length);
                setTotalPages(Math.ceil(allUsers.length / pageSize));

            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users. Please try again.');
            setUsers([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setPaginating(false);
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, roleFilter, statusFilter]);

    // CRUD Operations
    const handleCreateUser = async (userData) => {
        try {
            setActionLoading(true);

            // Debug: Log the data being sent
            console.log('Creating user with data:', userData);

            const response = await systemService.create(userData);

            if (response.success) {
                await fetchUsers();
                setIsCreatingUser(false);
                setEditingUser(null);
                setError(''); // Clear any existing errors
            } else {
                throw new Error(response.message || 'Failed to create user');
            }
        } catch (err) {
            console.error('Error creating user:', err);
            console.error('Full error object:', err);

            // Check if it's a permission error
            if (err.message.includes('Authentication failed') || err.message.includes('permission')) {
                setError('You do not have permission to create users. Only Super Admins can perform this action.');
            } else if (err.message.includes('already exists')) {
                setError('A user with this email already exists.');
            } else {
                setError(err.message || 'Failed to create user. Please check your input and try again.');
            }

            // Don't close the form on error so user can fix the issue
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateUser = async (userData) => {
        if (!editingUser) return;

        try {
            setActionLoading(true);

            const response = await systemService.update(editingUser.id, userData);

            if (response.success) {
                await fetchUsers();
                setEditingUser(null);

                if (selectedUser?.id === editingUser.id) {
                    // Refresh the selected user details
                    const updatedUserResponse = await systemService.getById(editingUser.id);
                    if (updatedUserResponse.success && updatedUserResponse.data) {
                        setSelectedUser(updatedUserResponse.data);
                    }
                }
            } else {
                throw new Error(response.message || 'Failed to update user');
            }
        } catch (err) {
            console.error('Error updating user:', err);
            setError(err.message || 'Failed to update user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteModal.user) return;

        try {
            setActionLoading(true);
            const response = await systemService.delete(deleteModal.user.id);

            if (response.success) {
                await fetchUsers();
                setDeleteModal({ open: false, user: null });

                if (selectedUser?.id === deleteModal.user.id) {
                    setSelectedUser(null);
                }
            } else {
                throw new Error(response.message || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            setError(err.message || 'Failed to delete user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSuspendUser = async (userId, reason) => {
        try {
            setActionLoading(true);
            const response = await systemService.suspend(userId, reason);

            if (response.success) {
                setError('');
                alert('User suspended. They can no longer sign in until reactivated.');
                await fetchUsers();
                if (selectedUser?.id === userId) {
                    const updatedUserResponse = await systemService.getById(userId);
                    if (updatedUserResponse.success && updatedUserResponse.data) {
                        setSelectedUser(updatedUserResponse.data);
                    }
                }
            } else {
                throw new Error(response.message || 'Failed to suspend user');
            }
        } catch (err) {
            console.error('Error suspending user:', err);
            setError(err.message || 'Failed to suspend user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReactivateUser = async (userId) => {
        try {
            setActionLoading(true);
            const response = await systemService.reactivate(userId);

            if (response.success) {
                setError('');
                alert('User reactivated. Their account is active and any lockout has been cleared.');
                await fetchUsers();
                if (selectedUser?.id === userId) {
                    const updatedUserResponse = await systemService.getById(userId);
                    if (updatedUserResponse.success && updatedUserResponse.data) {
                        setSelectedUser(updatedUserResponse.data);
                    }
                }
            } else {
                throw new Error(response.message || 'Failed to reactivate user');
            }
        } catch (err) {
            console.error('Error reactivating user:', err);
            setError(err.message || 'Failed to reactivate user');
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetPassword = async (userId) => {
        try {
            setActionLoading(true);
            const response = await systemService.resetPassword(userId);

            if (response.success) {
                setError(''); // Clear any errors
                // Show success message (you might want to add a toast notification here)
                alert('Password reset successfully. New password sent to user\'s email.');
            } else {
                throw new Error(response.message || 'Failed to reset password');
            }
        } catch (err) {
            console.error('Error resetting password:', err);
            setError(err.message || 'Failed to reset password');
        } finally {
            setActionLoading(false);
        }
    };

    // Pagination Component
    const Pagination = () => {
        const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
        const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

        return (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                        <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                        <span className="font-medium">{totalElements.toLocaleString()}</span> results
                    </span>

                    <CustomSelect
                        value={pageSize}
                        onChange={(val) => {
                            setPageSize(Number(val));
                            setCurrentPage(0);
                        }}
                        options={[
                            { value: 10, label: '10 per page' },
                            { value: 20, label: '20 per page' },
                            { value: 50, label: '50 per page' },
                            { value: 100, label: '100 per page' }
                        ]}
                        dropUp
                    />
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setCurrentPage(0)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="First page"
                    >
                        <ChevronsLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 0 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Previous page"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <span className="px-4 py-2 text-sm text-gray-700">
                        Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                    </span>

                    <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Next page"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setCurrentPage(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1 || paginating}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        title="Last page"
                    >
                        <ChevronsRight className="w-5 h-5" />
                    </button>

                    {paginating && (
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />
                    )}
                </div>
            </div>
        );
    };

    // UI Handlers
    const handleAddUser = () => {
        setIsCreatingUser(true);
        setEditingUser(null);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsCreatingUser(false);
    };

    const handleViewUser = async (user) => {
        try {
            setLoading(true);
            const response = await systemService.getById(user.id);
            if (response.success && response.data) {
                setSelectedUser(response.data);
            } else {
                setError('Failed to load user details');
            }
        } catch (err) {
            console.error('Error loading user:', err);
            setSelectedUser(user); // Fallback to basic user data
        } finally {
            setLoading(false);
        }
    };

    const handleBackFromForm = () => {
        setIsCreatingUser(false);
        setEditingUser(null);
    };

    const handleBackFromDetails = () => {
        setSelectedUser(null);
    };

    const handleToggleStatus = async (user) => {
        try {
            setActionLoading(true);
            const isActive = user.status === 'ACTIVE';

            // ACTIVE -> suspend; LOCKED/SUSPENDED -> reactivate (also clears any lockout)
            const response = isActive
                ? await systemService.toggleStatus(user.id)
                : await systemService.reactivate(user.id);

            if (response.success) {
                setError('');
                const name = `${user.firstName} ${user.lastName}`;
                alert(
                    isActive ? `${name} has been suspended.`
                        : user.status === 'LOCKED' ? `${name} has been unlocked.`
                            : `${name} has been reactivated.`
                );
                await fetchUsers();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (err) {
            console.error('Error updating status:', err);
            setError(err.message || 'Failed to update status');
        } finally {
            setActionLoading(false);
        }
    };

    // Show form page if creating or editing
    if (isCreatingUser || editingUser) {
        return (
            <SystemUserFormPage
                user={editingUser}
                onBack={handleBackFromForm}
                onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
                loading={actionLoading}
                error={error}
            />
        );
    }

    // Show user details view if selected
    if (selectedUser) {
        return (
            <SystemUserDetailsView
                user={selectedUser}
                onBack={handleBackFromDetails}
                onEdit={handleEditUser}
                onDelete={(user) => setDeleteModal({ open: true, user })}
                onSuspend={handleSuspendUser}
                onReactivate={handleReactivateUser}
                onResetPassword={handleResetPassword}
                onUserUpdated={async (updated) => {
                    if (updated) setSelectedUser(updated);
                    await fetchUsers();
                }}
                hasPermission={hasPermission}
            />
        );
    }

    // Show loading state for initial load only
    if (loading && users.length === 0) {
        return <LoadingSpinner message="Loading system users..." />;
    }

    // Main users list view
    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">System Users</h2>
                    <p className="text-gray-600 mt-1">
                        {totalElements.toLocaleString()} total users
                    </p>
                </div>
                {hasPermission('USERS:CREATE') && (
                    <button
                        onClick={handleAddUser}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Add User
                    </button>
                )}
            </div>

            {/* Error Alert */}
            {error && <ErrorAlert error={error} onRetry={fetchUsers} />}

            {/* User Stats */}
            {stats && (
                <SystemUserStats
                    stats={stats}
                    statusFilter={statusFilter}
                    onStatusChange={(status) => { setStatusFilter(status); setCurrentPage(0); }}
                />
            )}

            {/* Filters */}
            <SystemUserFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                roleFilter={roleFilter}
                onRoleChange={setRoleFilter}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                roles={roles}
            />

            {/* Users List with Pagination */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <SystemUsersList
                    users={users}
                    catalog={catalog}
                    onView={handleViewUser}
                    onEdit={handleEditUser}
                    onDelete={(user) => setDeleteModal({ open: true, user })}
                    onToggleStatus={handleToggleStatus}
                />

                {/* Pagination Controls */}
                {totalPages > 1 && <Pagination />}
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, user: null })}
                onConfirm={handleDeleteUser}
                itemName={deleteModal.user ?
                    `${deleteModal.user.firstName} ${deleteModal.user.lastName}` : ''}
                itemType="user"
                loading={actionLoading}
            />
        </div>
    );
};

export default SystemUsersContent;
